import type { ApprovalHistoryEntry } from '@/types/purchase-request';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  FileText, 
  RotateCcw, 
  Send, 
  Mail,
  Circle
} from 'lucide-react';
import { format } from 'date-fns';

interface ApprovalHistoryTimelineProps {
  approvalHistory: ApprovalHistoryEntry[];
  requestId?: string;
}

const getActionIcon = (action: string) => {
  const actionLower = action.toLowerCase();
  
  if (actionLower.includes('approve')) return CheckCircle2;
  if (actionLower.includes('reject')) return XCircle;
  if (actionLower.includes('submit')) return FileText;
  if (actionLower.includes('redirect') || actionLower.includes('reopen')) return RotateCcw;
  if (actionLower.includes('send') || actionLower.includes('forward')) return Send;
  if (actionLower.includes('post')) return Mail;
  
  return Circle;
};

const getActionColor = (action: string) => {
  const actionLower = action.toLowerCase();
  
  if (actionLower.includes('approve')) return 'text-success-600';
  if (actionLower.includes('reject')) return 'text-error-600';
  if (actionLower.includes('submit')) return 'text-blue-600';
  if (actionLower.includes('redirect') || actionLower.includes('reopen')) return 'text-orange-600';
  if (actionLower.includes('post')) return 'text-purple-600';
  
  return 'text-neutral-600';
};

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  const statusLower = status.toLowerCase();
  
  if (statusLower.includes('approve')) return 'default';
  if (statusLower.includes('reject')) return 'destructive';
  if (statusLower.includes('pending')) return 'secondary';
  
  return 'outline';
};

export function ApprovalHistoryTimeline({ approvalHistory, requestId }: ApprovalHistoryTimelineProps) {
  if (!approvalHistory || approvalHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No approval history available</h3>
        <p className="text-sm text-muted-foreground">
          This purchase request doesn't have any approval history yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="pb-4 border-b">
        <p className="text-sm text-muted-foreground">
          {approvalHistory.length} {approvalHistory.length === 1 ? 'event' : 'events'}
        </p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {approvalHistory.map((entry, index) => {
          const Icon = getActionIcon(entry.action);
          const iconColor = getActionColor(entry.action);
          const isLast = index === approvalHistory.length - 1;
          
          return (
            <div key={index} className="relative">
              {/* Timeline connector line */}
              {!isLast && (
                <div 
                  className="absolute left-5 top-12 bottom-0 w-0.5 bg-border"
                  style={{ height: '2.5rem' }}
                />
              )}
              
              {/* Event card */}
              <div className="flex gap-4 mb-4">
                {/* Icon */}
                <div className={`flex-shrink-0 ${iconColor}`}>
                  <Icon className="h-10 w-10" />
                </div>
                
                {/* Card content */}
                <div className="flex-1 bg-card border rounded-lg p-4 shadow-sm">
                  {/* Date */}
                  <div className="text-sm text-muted-foreground mb-2">
                    {format(new Date(entry.date), 'MMM dd, yyyy h:mm a')}
                  </div>
                  
                  {/* Action */}
                  <div className="font-semibold mb-3 text-base">
                    {entry.action}
                  </div>
                  
                  {/* Details grid */}
                  <div className="grid gap-2 text-sm">
                    {/* By User */}
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground min-w-[60px]">👤 By:</span>
                      <span className="font-medium">{entry.byUser}</span>
                    </div>
                    
                    {/* To User */}
                    {entry.toUser && (
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground min-w-[60px]">📤 To:</span>
                        <span className="font-medium">
                          {Array.isArray(entry.toUser) 
                            ? entry.toUser.length > 3
                              ? `${entry.toUser.slice(0, 3).join(', ')} +${entry.toUser.length - 3} more`
                              : entry.toUser.join(', ')
                            : entry.toUser
                          }
                        </span>
                      </div>
                    )}
                    
                    {/* Stage */}
                    {entry.stage && (
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground min-w-[60px]">🎯 Stage:</span>
                        <span className="font-medium">{entry.stage}</span>
                      </div>
                    )}
                    
                    {/* Status */}
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground min-w-[60px]">Status:</span>
                      <Badge variant={getStatusVariant(entry.status)}>
                        {entry.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
